import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createVisionCommand,
  getVisionDeviceStatus,
  listVisionCommands,
  listVisionEvents,
  VisionCommand,
  VisionDeviceStatus,
  VisionEvent,
} from '../api/client';

const DEFAULT_ROAST_LEVELS = [
  { label: 'Coffee 1',  color: '#748060', scale: 95 },
  { label: 'Coffee 2',  color: '#917D60', scale: 85 },
  { label: 'Coffee 3',  color: '#846A52', scale: 75 },
  { label: 'Coffee 4',  color: '#745642', scale: 65 },
  { label: 'Coffee 5',  color: '#5E4231', scale: 55 },
  { label: 'Coffee 6',  color: '#483227', scale: 45 },
  { label: 'Coffee 7',  color: '#332621', scale: 35 },
  { label: 'Coffee 8',  color: '#221D1B', scale: 25 },
  { label: 'Coffee 9',  color: '#1B1817', scale: 15 },
  { label: 'Coffee 10', color: '#141414', scale:  5 },
];

function rgbToHex(rgb: number[]): string {
  return '#' + rgb.map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
}

function rgbCss(rgb?: number[]) {
  if (!rgb || rgb.length !== 3) return '#3d2411';
  return `rgb(${rgb.map((v) => Math.round(v)).join(',')})`;
}

function rgbText(rgb?: number[]) {
  if (!rgb || rgb.length !== 3) return '--';
  return rgb.map((v) => Math.round(v)).join(', ');
}

function formatTime(value?: string) {
  if (!value) return '--';
  return new Date(value).toLocaleString('he-IL');
}

function numberValue(value?: number, digits = 1) {
  return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(digits) : '--';
}

function featureNumber(features: Record<string, unknown> | undefined, key: string): number | undefined {
  const value = features?.[key];
  return typeof value === 'number' ? value : undefined;
}

function RoastScale({ score, label, scoreStd, levels }: { score?: number; label?: string; scoreStd?: number; levels: typeof DEFAULT_ROAST_LEVELS }) {
  const marker = Math.max(0, Math.min(100, score ?? 0));
  const sortedLevels = [...levels].sort((a, b) => a.scale - b.scale);
  const hasStd = typeof scoreStd === 'number' && scoreStd > 0;
  const bandLo = hasStd ? Math.max(0, marker - scoreStd!) : marker;
  const bandHi = hasStd ? Math.min(100, marker + scoreStd!) : marker;
  return (
    <div className="rounded-2xl bg-white border border-cream-300 shadow-soft p-5" dir="ltr">
      <div className="flex items-center justify-between mb-3" dir="rtl">
        <div>
          <h2 className="text-lg font-bold text-espresso-900">סולם צבעי קלייה</h2>
          <div className="text-sm text-espresso-500">
            {label || 'אין סיווג'} · {numberValue(score, 0)}/100
            {hasStd && <span className="ml-2 text-espresso-400">±{numberValue(scoreStd, 1)} פיזור</span>}
          </div>
        </div>
        <div className="text-3xl font-extrabold text-espresso-900">{numberValue(score, 0)}</div>
      </div>
      <div className="relative pt-6">
        {/* variance band */}
        {hasStd && (
          <div
            className="absolute top-6 bottom-0 bg-white/40 border-x border-red-300/60 pointer-events-none rounded-sm"
            style={{ left: `${bandLo}%`, width: `${bandHi - bandLo}%` }}
          />
        )}
        {/* needle */}
        <div className="absolute top-0 -translate-x-1/2 z-10" style={{ left: `${marker}%` }}>
          <div className="h-5 w-0.5 bg-red-700 mx-auto" />
          <div className="h-3 w-3 rounded-full bg-red-700 border-2 border-white shadow-medium" />
        </div>
        <div className="grid grid-cols-10 overflow-hidden rounded-lg border border-espresso-200 h-16">
          {sortedLevels.map((level) => (
            <div key={level.label} style={{ background: level.color }} title={`${level.label} · ${level.scale}`} />
          ))}
        </div>
      </div>
      <div className="grid grid-cols-10 gap-1 mt-2 text-[10px] leading-tight text-center text-espresso-700">
        {sortedLevels.map((level) => (
          <div key={level.label} className="min-w-0">
            <div className="font-bold truncate">{level.label}</div>
            <div className="text-espresso-500">{level.scale}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RoastHistogram({ values, levels }: { values?: number[]; levels: typeof DEFAULT_ROAST_LEVELS }) {
  const bins = values?.length ? values : [];
  if (!bins.length) {
    return <div className="h-40 rounded-xl bg-cream-100 flex items-center justify-center text-espresso-400">אין היסטוגרמת דרגות קלייה</div>;
  }
  const max = Math.max(...bins, 0.001);
  return (
    <div className="h-44 grid grid-cols-10 items-end gap-2 rounded-xl bg-cream-100 p-3" dir="ltr">
      {levels.map((level, index) => {
        const value = bins[index] || 0;
        return (
          <div key={level.label} className="h-full flex flex-col justify-end gap-2 min-w-0">
            <div
              className="rounded-t-md border border-black/10"
              style={{ height: `${Math.max(4, (value / max) * 100)}%`, background: level.color }}
              title={`${level.label}: ${(value * 100).toFixed(1)}%`}
            />
            <div className="text-[10px] text-center text-espresso-700 truncate">{(value * 100).toFixed(0)}%</div>
          </div>
        );
      })}
    </div>
  );
}

function HueHistogram({ values }: { values?: unknown }) {
  const bins = Array.isArray(values) ? values.map((v) => Number(v) || 0) : [];
  if (!bins.length) {
    return <div className="h-28 rounded-xl bg-cream-100 flex items-center justify-center text-espresso-400">אין היסטוגרמת גוון</div>;
  }
  const max = Math.max(...bins, 0.001);
  return (
    <div className="h-28 flex items-end gap-1 rounded-xl bg-cream-100 p-2" dir="ltr">
      {bins.map((value, index) => (
        <div
          key={index}
          className="flex-1 rounded-t bg-gold-700"
          style={{ height: `${Math.max(3, (value / max) * 100)}%` }}
          title={`${index}: ${(value * 100).toFixed(1)}%`}
        />
      ))}
    </div>
  );
}

function MetricCard({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="rounded-xl bg-white border border-cream-300 shadow-soft p-4">
      <div className="text-sm text-espresso-500">{label}</div>
      <div className="text-3xl font-extrabold text-espresso-900 mt-1">{value}</div>
      {detail && <div className="text-xs text-espresso-500 mt-1">{detail}</div>}
    </div>
  );
}

function EventCard({ event, active, onClick }: { event: VisionEvent; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-right rounded-xl border p-3 transition-colors ${
        active ? 'bg-gold-100 border-gold-600' : 'bg-white border-cream-300 hover:bg-cream-50'
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="font-bold text-espresso-900">{formatTime(event.captured_at)}</span>
        {event.stop_recommended && <span className="rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-xs font-bold">עצירה</span>}
      </div>
      <div className="mt-1 text-sm text-espresso-500">
        {event.device_id} · {event.roast_level_label || 'ללא דרגה'} · {numberValue(event.roast_level_score, 0)}
      </div>
    </button>
  );
}

function CameraModal({
  deviceId,
  onClose,
  onCommand,
}: {
  deviceId: string;
  onClose: () => void;
  onCommand: () => void;
}) {
  const [reference, setReference] = useState('#8a6a45');
  const [roi, setRoi] = useState({ x: 0, y: 0, width: 640, height: 360 });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageOk, setMessageOk] = useState(true);

  // Image preview state
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [roiPreviewUrl, setRoiPreviewUrl] = useState<string | null>(null);
  const [previewCapturedAt, setPreviewCapturedAt] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [imgNatural, setImgNatural] = useState({ w: 640, h: 360 });
  const imgContainerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startY: number } | null>(null);

  function showMsg(text: string, ok = true) {
    setMessage(text);
    setMessageOk(ok);
  }

  async function queue(type: Parameters<typeof createVisionCommand>[1], payload: Record<string, unknown> = {}) {
    setBusy(true);
    setMessage(null);
    try {
      await createVisionCommand(deviceId, type, payload);
      showMsg('הפקודה נשלחה לתור המרוחק — ממתין לביצוע…');
      onCommand();
    } catch (err) {
      console.error(err);
      showMsg('שליחת הפקודה נכשלה', false);
    } finally {
      setBusy(false);
    }
  }

  async function captureAndPreview() {
    setBusy(true);
    setCapturing(true);
    setMessage(null);
    const sentAt = new Date().toISOString();
    try {
      await createVisionCommand(deviceId, 'image.capture');
      onCommand();
      showMsg('פקודת צילום נשלחה — ממתין לתמונה מה-RPi…');
      // Poll for a new event that arrived after we sent the command
      let found = false;
      for (let i = 0; i < 20; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const events = await listVisionEvents(5);
        const newer = events.find(
          (e) => e.full_image_url && e.captured_at > sentAt,
        );
        if (newer) {
          setPreviewUrl(newer.full_image_url || null);
          setRoiPreviewUrl(newer.roi_image_url || null);
          setPreviewCapturedAt(newer.captured_at);
          if (newer.features?.roi && typeof newer.features.roi === 'object') {
            const r = newer.features.roi as Record<string, number>;
            setRoi({ x: r.x ?? 0, y: r.y ?? 0, width: r.width ?? 640, height: r.height ?? 360 });
          }
          showMsg('תמונה התקבלה בהצלחה');
          found = true;
          break;
        }
      }
      if (!found) showMsg('התמונה לא התקבלה — בדוק שה-RPi מחובר', false);
    } catch (err) {
      console.error(err);
      showMsg('שגיאה בשליחת פקודת הצילום', false);
    } finally {
      setBusy(false);
      setCapturing(false);
    }
  }

  async function autoCalibrateRoi() {
    setBusy(true);
    setMessage(null);
    const sentAt = new Date().toISOString();
    try {
      await createVisionCommand(deviceId, 'calibration.auto');
      onCommand();
      showMsg('כיול ROI אוטומטי נשלח — ממתין לתוצאה…');
      for (let i = 0; i < 20; i++) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const commands = await listVisionCommands(deviceId, 10);
        const done = commands.find(
          (command) => command.type === 'calibration.auto'
            && command.status === 'succeeded'
            && command.created_at > sentAt,
        );
        if (done?.result) {
          const result = done.result as Record<string, unknown>;
          if (result.roi && typeof result.roi === 'object') {
            const nextRoi = result.roi as Record<string, number>;
            setRoi({
              x: nextRoi.x ?? 0,
              y: nextRoi.y ?? 0,
              width: nextRoi.width ?? 640,
              height: nextRoi.height ?? 360,
            });
          }
          showMsg('כיול ROI אוטומטי הצליח — האזור עודכן');
          return;
        }
        const failed = commands.find(
          (command) => command.type === 'calibration.auto'
            && command.status === 'failed'
            && command.created_at > sentAt,
        );
        if (failed) {
          showMsg(`כיול ROI נכשל: ${failed.error || 'שגיאה לא ידועה'}`, false);
          return;
        }
      }
      showMsg('כיול ROI לא הסתיים בזמן', false);
    } catch (error) {
      console.error(error);
      showMsg('שגיאה בשליחת פקודת כיול ROI', false);
    } finally {
      setBusy(false);
    }
  }

  async function saveRoi() {
    setBusy(true);
    setMessage(null);
    const sentAt = new Date().toISOString();
    try {
      await createVisionCommand(deviceId, 'roi.update', roi);
      onCommand();
      showMsg('פקודת עדכון ROI נשלחה — ממתין לאישור…');
      for (let i = 0; i < 20; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const cmds = await listVisionCommands(deviceId, 10);
        const done = cmds.find(
          (c) => c.type === 'roi.update' && c.status === 'succeeded' && c.created_at > sentAt,
        );
        if (done) {
          showMsg('ה-ROI נשמר בהצלחה ✓');
          break;
        }
        const failed = cmds.find(
          (c) => c.type === 'roi.update' && c.status === 'failed' && c.created_at > sentAt,
        );
        if (failed) {
          showMsg(`שמירת ROI נכשלה: ${failed.error || 'שגיאה לא ידועה'}`, false);
          break;
        }
      }
    } catch (err) {
      console.error(err);
      showMsg('שגיאה בשליחת פקודת עדכון ROI', false);
    } finally {
      setBusy(false);
    }
  }

  // ROI overlay helpers — translate image pixel coords ↔ display coords
  function toDisplay(px: number, axis: 'x' | 'y') {
    const el = imgContainerRef.current;
    if (!el) return 0;
    const { width, height } = el.getBoundingClientRect();
    return axis === 'x' ? (px / imgNatural.w) * width : (px / imgNatural.h) * height;
  }
  function toImagePx(display: number, axis: 'x' | 'y') {
    const el = imgContainerRef.current;
    if (!el) return 0;
    const { width, height } = el.getBoundingClientRect();
    return Math.round(axis === 'x' ? (display / width) * imgNatural.w : (display / height) * imgNatural.h);
  }

  const roiStyle = previewUrl ? {
    left: `${toDisplay(roi.x, 'x')}px`,
    top: `${toDisplay(roi.y, 'y')}px`,
    width: `${toDisplay(roi.width, 'x')}px`,
    height: `${toDisplay(roi.height, 'y')}px`,
  } : null;

  const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!imgContainerRef.current) return;
    const rect = imgContainerRef.current.getBoundingClientRect();
    dragRef.current = { startX: e.clientX - rect.left, startY: e.clientY - rect.top };
  }, []);

  const onMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragRef.current || !imgContainerRef.current) return;
    const rect = imgContainerRef.current.getBoundingClientRect();
    const curX = e.clientX - rect.left;
    const curY = e.clientY - rect.top;
    const x = toImagePx(Math.min(dragRef.current.startX, curX), 'x');
    const y = toImagePx(Math.min(dragRef.current.startY, curY), 'y');
    const width = toImagePx(Math.abs(curX - dragRef.current.startX), 'x');
    const height = toImagePx(Math.abs(curY - dragRef.current.startY), 'y');
    if (width > 10 && height > 10) setRoi({ x, y, width, height });
  }, [imgNatural]);

  const onMouseUp = useCallback(() => { dragRef.current = null; }, []);

  const inputClass = 'input py-2';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl bg-cream-50 shadow-strong border border-cream-300">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-cream-300 bg-cream-50/95 p-5">
          <div>
            <h2 className="text-xl font-extrabold text-espresso-900">מצלמה וכיול</h2>
            <div className="text-sm text-espresso-500" dir="ltr">{deviceId}</div>
          </div>
          <button onClick={onClose} className="btn-secondary w-10 h-10 p-0" aria-label="סגור">×</button>
        </div>

        <div className="p-5 space-y-6">

          {/* ── Image preview + ROI ── */}
          <section className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="text-base font-extrabold text-espresso-900">תמונה חיה ו-ROI</h3>
              <button
                type="button"
                disabled={busy}
                onClick={captureAndPreview}
                className="btn-primary"
              >
                {capturing ? 'מצלם…' : 'צלם תמונה'}
              </button>
              {previewCapturedAt && (
                <span className="text-xs text-espresso-500">{formatTime(previewCapturedAt)}</span>
              )}
            </div>

            {previewUrl ? (
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_200px] gap-3">
                {/* Full frame with draggable ROI overlay */}
                <div className="space-y-1">
                  <div className="text-xs text-espresso-500 font-bold">גרור על התמונה לסמן ROI חדש</div>
                  <div
                    ref={imgContainerRef}
                    className="relative rounded-xl overflow-hidden border border-espresso-200 cursor-crosshair select-none"
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseUp}
                  >
                    <img
                      src={previewUrl}
                      alt="Camera frame"
                      className="w-full block"
                      onLoad={(e) => {
                        const img = e.currentTarget;
                        setImgNatural({ w: img.naturalWidth, h: img.naturalHeight });
                      }}
                      draggable={false}
                    />
                    {roiStyle && (
                      <div
                        className="absolute border-2 border-gold-500 bg-gold-400/10 pointer-events-none"
                        style={roiStyle}
                      >
                        <div className="absolute -top-5 left-0 text-[10px] font-bold text-gold-700 bg-white/80 px-1 rounded">
                          ROI
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* ROI crop preview */}
                <div className="space-y-1">
                  <div className="text-xs text-espresso-500 font-bold">אזור ROI</div>
                  {roiPreviewUrl ? (
                    <img src={roiPreviewUrl} alt="ROI" className="w-full rounded-xl border border-espresso-200 object-contain" />
                  ) : (
                    <div className="h-32 rounded-xl bg-cream-100 flex items-center justify-center text-xs text-espresso-400">
                      אין תמונת ROI
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="h-52 rounded-xl bg-cream-100 border-2 border-dashed border-cream-300 flex flex-col items-center justify-center gap-2 text-espresso-400">
                <div className="text-4xl">📷</div>
                <div>לחץ "צלם תמונה" כדי לקבל תמונה מה-RPi</div>
              </div>
            )}

            {/* ROI numeric inputs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
              {(['x', 'y', 'width', 'height'] as const).map((key) => (
                <label key={key} className="space-y-1">
                  <span className="text-xs font-bold text-espresso-700" dir="ltr">{key}</span>
                  <input
                    className={inputClass}
                    type="number"
                    min={0}
                    value={roi[key]}
                    onChange={(e) => setRoi((c) => ({ ...c, [key]: Number(e.target.value) }))}
                  />
                </label>
              ))}
              <button
                type="button"
                disabled={busy}
                onClick={() => saveRoi()}
                className="btn-primary"
              >
                שמור ROI
              </button>
            </div>
          </section>

          {/* ── Calibration ── */}
          <section className="rounded-xl bg-white border border-cream-300 p-4 space-y-3">
            <h3 className="text-base font-extrabold text-espresso-900">כיול</h3>
            <div className="flex flex-wrap gap-3">
              <button type="button" disabled={busy} onClick={autoCalibrateRoi} className="btn-primary">
                כיול ROI אוטומטי
              </button>
              <button type="button" disabled={busy} onClick={() => queue('calibration.capture')} className="btn-secondary">
                צלם לכיול ידני
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
              <label className="space-y-1">
                <span className="text-xs font-bold text-espresso-700">צבע ייחוס (hex)</span>
                <div className="flex gap-2 items-center">
                  <span
                    className="w-8 h-8 rounded border border-espresso-200 shrink-0"
                    style={{ background: reference }}
                  />
                  <input
                    className={inputClass}
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    dir="ltr"
                  />
                </div>
              </label>
              <button
                type="button"
                disabled={busy}
                onClick={() => queue('calibration.apply', { reference: { hex: reference } })}
                className="btn-secondary"
              >
                החל כיול ידני
              </button>
            </div>
          </section>

          <section className="rounded-xl bg-white border border-cream-300 p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-extrabold text-espresso-900">הגדרות מצלמה וכיול חשיפה</h3>
              <p className="text-xs text-espresso-500">הגדרות נשמרות, גרסאות, צילום בדיקה וכיול מנוהלים במסך הייעודי.</p>
            </div>
            <Link to={`/camera-settings?deviceId=${encodeURIComponent(deviceId)}`} className="btn-primary" onClick={onClose}>
              פתח הגדרות מצלמה
            </Link>
          </section>

          {message && (
            <div className={`rounded-xl border p-3 text-sm font-bold ${
              messageOk ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'
            }`}>
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function deviceOnline(status: VisionDeviceStatus | null): 'online' | 'stale' | 'offline' {
  if (!status) return 'offline';
  const ref = status.last_event_at || status.last_command_interaction_at;
  if (!ref) return 'offline';
  const ageSeconds = (Date.now() - new Date(ref).getTime()) / 1000;
  if (ageSeconds < 30) return 'online';
  if (ageSeconds < 120) return 'stale';
  return 'offline';
}

function RoastLabelsPanel({
  deviceId,
  onCommand,
  onLevelsChange,
}: {
  deviceId: string;
  onCommand: () => void;
  onLevelsChange: (levels: typeof DEFAULT_ROAST_LEVELS) => void;
}) {
  const [labels, setLabels] = useState<string[]>(DEFAULT_ROAST_LEVELS.map(l => l.label));
  const [colors, setColors] = useState<string[]>(DEFAULT_ROAST_LEVELS.map(l => l.color));
  const [scales, setScales] = useState<number[]>(DEFAULT_ROAST_LEVELS.map(l => l.scale));
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [msgOk, setMsgOk] = useState(true);
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    async function loadLevels() {
      setLoading(true);
      try {
        const sentAt = new Date().toISOString();
        await createVisionCommand(deviceId, 'levels.get', {});
        for (let i = 0; i < 15; i++) {
          await new Promise(r => setTimeout(r, 1500));
          const cmds = await listVisionCommands(deviceId, 10);
          const done = cmds.find(
            c => c.type === 'levels.get' && c.status === 'succeeded' && c.created_at > sentAt
          );
          if (done?.result?.levels && Array.isArray(done.result.levels)) {
            if (cancelled) return;
            const lvls = done.result.levels as Array<{ label: string; rgb: number[]; scale?: number }>;
            setLabels(lvls.map(l => l.label));
            const newColors = lvls.map(l => l.rgb ? rgbToHex(l.rgb) : DEFAULT_ROAST_LEVELS[0].color);
            setColors(newColors);
            const newScales = lvls.map((l, idx) => typeof l.scale === 'number' ? l.scale : DEFAULT_ROAST_LEVELS[idx].scale);
            setScales(newScales);
            onLevelsChange(lvls.map((l, idx) => ({ label: l.label, color: newColors[idx], scale: newScales[idx] })));
            break;
          }
          const failed = cmds.find(
            c => c.type === 'levels.get' && c.status === 'failed' && c.created_at > sentAt
          );
          if (failed) break;
        }
      } catch {
        // RPi offline — use defaults silently
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadLevels();
    return () => { cancelled = true; };
  }, [deviceId]);

  function startEdit(i: number) {
    setEditingIdx(i);
    setEditValue(labels[i]);
    setTimeout(() => editInputRef.current?.select(), 0);
  }

  function commitEdit() {
    if (editingIdx === null) return;
    const val = editValue.trim();
    if (val) setLabels(prev => prev.map((l, j) => j === editingIdx ? val : l));
    setEditingIdx(null);
  }

  async function handleSave() {
    setBusy(true);
    setMsg('');
    try {
      await createVisionCommand(deviceId, 'calibration.set_labels', { labels, scales });
      onCommand();
      onLevelsChange(labels.map((label, i) => ({ label, color: colors[i], scale: scales[i] })));
      setMsg('התוויות נשמרו בהצלחה');
      setMsgOk(true);
    } catch {
      setMsg('שגיאה בשמירת התוויות');
      setMsgOk(false);
    } finally {
      setBusy(false);
    }
  }

  // 5×2 grid matching the physical calibration card layout
  const rows = [labels.slice(0, 5), labels.slice(5, 10)];
  const rowColors = [colors.slice(0, 5), colors.slice(5, 10)];
  const rowScales = [scales.slice(0, 5), scales.slice(5, 10)];

  return (
    <div className="bg-white rounded-2xl p-5 shadow-soft border border-cream-300">
      <div className="flex items-center justify-between mb-4" dir="rtl">
        <div>
          <h2 className="text-lg font-bold text-espresso-900">תוויות וערכי סולם הקלייה</h2>
          <p className="text-xs text-espresso-500 mt-0.5">לחץ על ריבוע לעריכת התווית · הזן מספר סולם קפה לכל צבע</p>
        </div>
        {loading && <span className="text-xs text-espresso-400">טוען מה-RPi…</span>}
      </div>

      <div className="space-y-2" dir="ltr">
        {rows.map((rowLabels, rowIdx) => (
          <div key={rowIdx} className="grid grid-cols-5 gap-2">
            {rowLabels.map((label, colIdx) => {
              const i = rowIdx * 5 + colIdx;
              const isEditing = editingIdx === i;
              const isDark = i >= 5;
              return (
                <div key={i} className="flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => startEdit(i)}
                    className={`w-full aspect-square rounded-lg border-2 transition-all ${
                      isEditing ? 'border-gold-500 ring-2 ring-gold-300' : 'border-transparent hover:border-espresso-300'
                    }`}
                    style={{ background: rowColors[rowIdx][colIdx] }}
                    title={`Click to rename: ${label}`}
                  />
                  {isEditing ? (
                    <input
                      ref={editInputRef}
                      type="text"
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onBlur={commitEdit}
                      onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditingIdx(null); }}
                      className="text-xs text-center border border-gold-500 rounded px-1 py-0.5 w-full focus:outline-none focus:ring-1 focus:ring-gold-400"
                      autoFocus
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => startEdit(i)}
                      className={`text-[10px] text-center font-medium truncate w-full px-0.5 rounded hover:bg-cream-100 ${
                        isDark ? 'text-espresso-600' : 'text-espresso-700'
                      }`}
                    >
                      {label}
                    </button>
                  )}
                  <input
                    type="number"
                    inputMode="numeric"
                    step={1}
                    min={0}
                    max={100}
                    value={rowScales[rowIdx][colIdx]}
                    onChange={e => {
                      const raw = e.target.value;
                      const parsed = raw === '' ? 0 : parseInt(raw, 10);
                      if (Number.isNaN(parsed)) return;
                      const clamped = Math.max(0, Math.min(100, parsed));
                      setScales(prev => prev.map((s, j) => j === i ? clamped : s));
                    }}
                    className="text-xs text-center border border-cream-300 rounded px-1 py-0.5 w-full focus:outline-none focus:ring-1 focus:ring-gold-400 focus:border-gold-500"
                    title="מספר סולם הקפה המדעי"
                    aria-label={`Coffee scale number for ${label}`}
                  />
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-4" dir="rtl">
        <button onClick={handleSave} disabled={busy || loading} className="btn-primary">
          {busy ? 'שומר…' : 'שמור תוויות'}
        </button>
        {msg && (
          <span className={`text-sm font-bold ${msgOk ? 'text-green-700' : 'text-red-600'}`}>{msg}</span>
        )}
      </div>
    </div>
  );
}

function Vision() {
  const [events, setEvents] = useState<VisionEvent[]>([]);
  const [commands, setCommands] = useState<VisionCommand[]>([]);
  const [deviceStatus, setDeviceStatus] = useState<VisionDeviceStatus | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [followLatest, setFollowLatest] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [roastLevels, setRoastLevels] = useState(DEFAULT_ROAST_LEVELS);

  // While following live, always show the newest event so the image/metrics refresh
  // with every poll (same idea as the coffee-scale indicator). Selecting an older
  // event from the list pauses following until the latest event is picked again.
  const selected = useMemo(
    () => (followLatest ? events[0] : events.find((event) => event.id === selectedId) || events[0]),
    [events, selectedId, followLatest]
  );
  const deviceId = selected?.device_id || events[0]?.device_id || 'rpi-vision-001';

  async function loadEvents() {
    try {
      setError(null);
      const data = await listVisionEvents(50);
      setEvents(data);
      setSelectedId((current) => current || data[0]?.id || null);
    } catch (err) {
      console.error(err);
      setError('לא ניתן לטעון נתוני ראייה');
    } finally {
      setLoading(false);
    }
  }

  async function loadCommands(currentDeviceId = deviceId) {
    if (!currentDeviceId) return;
    try {
      setCommands(await listVisionCommands(currentDeviceId, 10));
    } catch (err) {
      console.error(err);
    }
  }

  async function loadDeviceStatus(currentDeviceId = deviceId) {
    if (!currentDeviceId) return;
    try {
      setDeviceStatus(await getVisionDeviceStatus(currentDeviceId));
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    loadEvents();
    const interval = setInterval(() => { if (!document.hidden) loadEvents(); }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadCommands(deviceId);
    loadDeviceStatus(deviceId);
    const interval = setInterval(() => {
      loadCommands(deviceId);
      loadDeviceStatus(deviceId);
    }, 5000);
    return () => clearInterval(interval);
  }, [deviceId]);

  const roastScore = selected?.roast_level_score ?? featureNumber(selected?.features, 'roast_level_score') ?? selected?.brownness_score;
  const roastLabel = selected?.roast_level_label || String(selected?.features?.roast_level_label || '');
  const roastHistogram = selected?.roast_level_histogram || (selected?.features?.roast_level_histogram as number[] | undefined);
  const roastScoreStd = featureNumber(selected?.features, 'roast_score_std');
  const colorVarianceLab = featureNumber(selected?.features, 'color_variance_lab');

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-espresso-900">ראיית קלייה</h1>
          <div className="text-sm text-espresso-600 mt-1">ניתוח צבעי פולים ושליטה מרחוק במצלמה</div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setModalOpen(true)} className="btn-primary">מצלמה וכיול</button>
          <button onClick={loadEvents} className="btn-secondary">רענון</button>
        </div>
      </div>

      {(() => {
        const connectivity = deviceOnline(deviceStatus);
        const dotColor = connectivity === 'online' ? 'bg-green-500' : connectivity === 'stale' ? 'bg-yellow-400' : 'bg-red-400';
        const label = connectivity === 'online' ? 'מחובר' : connectivity === 'stale' ? 'עצלן' : 'לא מחובר';
        const lastSeen = deviceStatus?.last_event_at || deviceStatus?.last_command_interaction_at;
        const pending = deviceStatus?.pending_commands ?? 0;
        return (
          <div className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-2 text-sm ${
            connectivity === 'online' ? 'bg-green-50 border-green-200' :
            connectivity === 'stale' ? 'bg-yellow-50 border-yellow-200' :
            'bg-red-50 border-red-200'
          }`}>
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${dotColor}`} />
            <span className="font-bold text-espresso-800">{deviceId} — {label}</span>
            {lastSeen && <span className="text-espresso-500">נראה לאחרונה: {formatTime(lastSeen)}</span>}
            {pending > 0 && (
              <span className="rounded-full bg-yellow-100 text-yellow-800 px-2 py-0.5 font-bold">
                {pending} פקודות ממתינות
              </span>
            )}
            {connectivity !== 'online' && (
              <span className="text-espresso-500 text-xs">
                אין תגובה מה-RPi — בדוק שהשירות פועל ומחובר לאינטרנט
              </span>
            )}
          </div>
        );
      })()}

      {error && <div className="rounded-xl bg-red-50 text-red-700 p-4 font-bold">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-gold-700" />
        </div>
      ) : !selected ? (
        <div className="bg-white rounded-2xl p-10 text-center text-espresso-400 text-xl">
          עדיין אין תמונות או נתוני צבע מה-Raspberry Pi
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[310px_1fr] gap-5 min-h-0">
          <aside className="space-y-3 overflow-y-auto max-h-[calc(100vh-190px)] pl-2">
            {events.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                active={event.id === selected.id}
                onClick={() => {
                  setSelectedId(event.id);
                  setFollowLatest(event.id === events[0]?.id);
                }}
              />
            ))}
          </aside>

          <section className="space-y-5">
            <div className={`rounded-2xl p-5 shadow-soft border ${
              selected.stop_recommended ? 'bg-red-50 border-red-300' : 'bg-white border-cream-300'
            }`}>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="text-sm text-espresso-500">אירוע אחרון</div>
                  <div className="text-2xl font-extrabold text-espresso-900">{formatTime(selected.captured_at)}</div>
                  <div className="text-sm text-espresso-500 mt-1">
                    {selected.device_id} · סשן {selected.session_id || '--'} · {selected.calibrated ? 'מכויל' : 'לא מכויל'}
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div className="text-xs font-bold text-espresso-500 mb-0.5">ניקוד קלייה</div>
                    <div className="text-5xl font-extrabold text-espresso-900 leading-none">{numberValue(roastScore, 0)}</div>
                    <div className="text-sm text-espresso-500 mt-1">{roastLabel || '--'} / 100</div>
                  </div>
                  <div className={`text-3xl font-extrabold ${selected.stop_recommended ? 'text-red-700' : 'text-green-700'}`}>
                    {selected.stop_recommended ? 'לעצור קלייה' : 'ממשיך'}
                  </div>
                </div>
              </div>
            </div>

            <RoastScale score={roastScore} label={roastLabel} scoreStd={roastScoreStd} levels={roastLevels} />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard label="דרגת קלייה" value={roastLabel || '--'} detail="הדרגה הקרובה בסולם" />
              <MetricCard label="מספר צבע" value={numberValue(roastScore, 0)} detail="0 ירוק, 100 כהה" />
              <MetricCard label="מרחק מהיעד" value={numberValue(selected.target_distance, 1)} />
              <MetricCard label="זמן קלייה" value={`${Math.round(selected.roast_time_seconds || 0)}s`} />
              <MetricCard
                label="פיזור צבע (Lab)"
                value={numberValue(colorVarianceLab, 1)}
                detail="אחידות צבע הפולים"
              />
              <MetricCard
                label="פיזור סולם ±"
                value={roastScoreStd != null ? `±${numberValue(roastScoreStd, 1)}` : '--'}
                detail="טווח ציוני קלייה"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
              <div className="rounded-2xl bg-white border border-cream-300 shadow-soft p-5">
                <h2 className="text-lg font-bold text-espresso-900 mb-3">התפלגות ירוק עד כהה</h2>
                <RoastHistogram values={roastHistogram} levels={roastLevels} />
              </div>
              <div className="rounded-2xl bg-white border border-cream-300 shadow-soft p-5 space-y-4">
                <h2 className="text-lg font-bold text-espresso-900">צבעים מחושבים</h2>
                {[
                  ['צבע פולים נוכחי', selected.average_rgb],
                  ['צפוי אחרי קירור', selected.predicted_cooled_rgb],
                  ['יעד אחרי קירור', selected.target_cooled_rgb],
                ].map(([label, rgb]) => (
                  <div key={label as string} className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-bold text-espresso-700">{label as string}</div>
                      <div className="font-mono text-sm text-espresso-500" dir="ltr">{rgbText(rgb as number[] | undefined)}</div>
                    </div>
                    <div className="w-16 h-10 rounded-lg border border-espresso-200 shadow-inner" style={{ background: rgbCss(rgb as number[] | undefined) }} />
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl p-4 shadow-soft border border-cream-300">
                <h2 className="text-lg font-bold text-espresso-900 mb-3">תמונה מלאה</h2>
                {selected.full_image_url ? (
                  <>
                    <div className="relative aspect-[16/9] w-full rounded-xl overflow-hidden bg-cream-100">
                      <img src={selected.full_image_url} alt="Full roast camera frame" className="absolute inset-0 w-full h-full object-contain" />
                      {(() => {
                        const roi = selected.features?.roi as { x: number; y: number; width: number; height: number } | undefined;
                        const fw = (selected.features?.frame_width as number | undefined) ?? 2560;
                        const fh = (selected.features?.frame_height as number | undefined) ?? 1440;
                        if (!roi) return null;
                        return (
                          <div
                            className="absolute pointer-events-none"
                            style={{
                              left: `${(roi.x / fw) * 100}%`,
                              top: `${(roi.y / fh) * 100}%`,
                              width: `${(roi.width / fw) * 100}%`,
                              height: `${(roi.height / fh) * 100}%`,
                              boxShadow: '0 0 0 1.5px black, inset 0 0 0 1.5px black',
                              outline: '1.5px solid white',
                            }}
                          />
                        );
                      })()}
                      {selected.captured_at && (
                        <div className="absolute bottom-0 inset-x-0 rounded-b-xl bg-black/55 px-2 py-1.5 flex items-center justify-center gap-2">
                          <span className="text-xs font-bold text-white tabular-nums">
                            {new Date(selected.captured_at).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </span>
                          <span className="text-xs text-white/70">
                            {new Date(selected.captured_at).toLocaleDateString('he-IL')}
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="h-72 rounded-xl bg-cream-100 flex items-center justify-center text-espresso-400">אין תמונה מלאה</div>
                )}
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-soft border border-cream-300">
                <h2 className="text-lg font-bold text-espresso-900 mb-3">אזור הפולים</h2>
                {selected.roi_image_url ? (
                  <>
                    <div className="relative">
                      <img src={selected.roi_image_url} alt="Cropped bean ROI" className="w-full rounded-xl bg-cream-100 object-contain max-h-[420px]" />
                      {selected.captured_at && (
                        <div className="absolute bottom-0 inset-x-0 rounded-b-xl bg-black/55 px-2 py-1.5 flex items-center justify-center gap-2">
                          <span className="text-xs font-bold text-white tabular-nums">
                            {new Date(selected.captured_at).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </span>
                          <span className="text-xs text-white/70">
                            {new Date(selected.captured_at).toLocaleDateString('he-IL')}
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="h-72 rounded-xl bg-cream-100 flex items-center justify-center text-espresso-400">אין תמונת ROI</div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl p-5 shadow-soft border border-cream-300">
                <h2 className="text-lg font-bold text-espresso-900 mb-3">היסטוגרמת גוון קיימת</h2>
                <HueHistogram values={selected.features?.hue_histogram} />
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-soft border border-cream-300">
                <h2 className="text-lg font-bold text-espresso-900 mb-3">פקודות אחרונות</h2>
                <div className="space-y-2">
                  {commands.length ? commands.map((command) => (
                    <div key={command.id} className="flex items-center justify-between gap-3 rounded-lg bg-cream-100 p-3">
                      <div className="min-w-0">
                        <div className="font-bold text-espresso-900 truncate" dir="ltr">{command.type}</div>
                        <div className="text-xs text-espresso-500">{formatTime(command.created_at)}</div>
                      </div>
                      <span className={`rounded-full px-2 py-1 text-xs font-bold ${
                        command.status === 'succeeded' ? 'bg-green-100 text-green-700' :
                          command.status === 'failed' ? 'bg-red-100 text-red-700' :
                            'bg-gold-100 text-espresso-800'
                      }`}>
                        {command.status}
                      </span>
                    </div>
                  )) : (
                    <div className="text-espresso-400">אין פקודות אחרונות</div>
                  )}
                </div>
              </div>
            </div>
            
            <RoastLabelsPanel
              deviceId={deviceId}
              onCommand={() => loadCommands(deviceId)}
              onLevelsChange={setRoastLevels}
            />
          </section>
        </div>
      )}

      {modalOpen && (
        <CameraModal
          deviceId={deviceId}
          onClose={() => setModalOpen(false)}
          onCommand={() => loadCommands(deviceId)}
        />
      )}
    </div>
  );
}

export default Vision;
